import React, { useState, useEffect } from 'react';
import SidebarHeader from '../../components/admin/sidebar';
import { useAuth } from '../../pages/auth/AuthContext';
import RequireAdminRole from '../../components/auth/userole';
import { 
  Search, 
  Filter, 
  RefreshCw, 
  Eye, 
  Calendar,
  Edit,
  MapPin,
  User,
  Package,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

// API configuration
const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

const STATUS_OPTIONS = [
  { value: '', label: 'Semua Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'survey_scheduled', label: 'Survey Scheduled' },
  { value: 'survey_completed', label: 'Survey Completed' },
  { value: 'installation_scheduled', label: 'Installation Scheduled' },
  { value: 'installed', label: 'Installed' },
  { value: 'active', label: 'Active' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'suspended', label: 'Suspended' }
];

const AdminOrders = () => {
  const { user: contextUser, token: contextToken } = useAuth();
  const { user, loading } = useAuth();

  const [activeTab, setActiveTab] = useState('orders');
  const [isLoading, setIsLoading] = useState(false);
  const [orders, setOrders] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [currentUser, setCurrentUser] = useState({
    name: contextUser?.fullName || contextUser?.name || 'Admin User',
    email: contextUser?.email || 'admin@telnet.co.id',
    role: contextUser?.role || 'Super Admin'
  });
  
  // Modal states
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [scheduleData, setScheduleData] = useState({
    surveyDate: '',
    installationDate: '',
    activationDate: ''
  });

  const LIMIT = 10;

  // Token handling
  const authToken = contextToken || 
                   localStorage.getItem('adminToken') || 
                   localStorage.getItem('token') ||
                   sessionStorage.getItem('adminToken') ||
                   sessionStorage.getItem('token');

  // Auth headers function
  const getAuthHeaders = () => {
    const headers = {
      'Content-Type': 'application/json'
    };
    
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    return headers;
  };

  // Format currency IDR
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  // Format currency short for mobile
  const formatCurrencyShort = (amount) => {
    if (amount >= 1000000000) {
      return `Rp ${(amount / 1000000000).toFixed(1)}B`;
    } else if (amount >= 1000000) {
      return `Rp ${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `Rp ${(amount / 1000).toFixed(1)}K`;
    }
    return formatCurrency(amount);
  };

  // Format tanggal
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Format address display
  const formatAddress = (address) => {
    if (!address) return 'N/A';
    
    const parts = [];
    if (address.fullAddress) parts.push(address.fullAddress);
    if (address.city) parts.push(address.city);
    if (address.province) parts.push(address.province);
    if (address.postalCode) parts.push(address.postalCode);
    
    return parts.join(', ') || 'Alamat tidak lengkap';
  };

  // Format address for mobile (shorter version)
  const formatAddressShort = (address) => {
    if (!address) return 'N/A';
    
    const parts = [];
    if (address.city) parts.push(address.city);
    if (address.province) parts.push(address.province);
    
    return parts.join(', ') || address.fullAddress || 'N/A';
  };

  // Status badge color - Green theme
  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800 border-green-200',
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      confirmed: 'bg-blue-100 text-blue-800 border-blue-200',
      cancelled: 'bg-red-100 text-red-800 border-red-200',
      installation_scheduled: 'bg-purple-100 text-purple-800 border-purple-200',
      survey_completed: 'bg-indigo-100 text-indigo-800 border-indigo-200',
      survey_scheduled: 'bg-indigo-200 text-indigo-900 border-indigo-300',
      installed: 'bg-teal-100 text-teal-800 border-teal-200',
      suspended: 'bg-gray-300 text-gray-700 border-gray-400'
    };
    return colors[status] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status) => {
    const icons = {
      active: <CheckCircle size={14} className="text-green-600" />,
      pending: <Clock size={14} className="text-yellow-600" />,
      confirmed: <CheckCircle size={14} className="text-blue-600" />,
      cancelled: <XCircle size={14} className="text-red-600" />,
      installation_scheduled: <Calendar size={14} className="text-purple-600" />,
      survey_completed: <CheckCircle size={14} className="text-indigo-600" />,
      survey_scheduled: <Calendar size={14} className="text-indigo-700" />,
      installed: <CheckCircle size={14} className="text-teal-600" />,
      suspended: <Clock size={14} className="text-gray-600" />
    };
    return icons[status] || <Clock size={14} className="text-gray-600" />;
  };

  // Fetch orders
  const fetchOrders = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: LIMIT.toString(),
        ...(statusFilter && { status: statusFilter }),
        ...(search && { search })
      });

      const headers = getAuthHeaders();
      const adminOrdersUrl = `${API_BASE_URL}/api/orders/all?${params.toString()}`;

      const response = await fetch(adminOrdersUrl, {
        method: 'GET',
        headers,
        credentials: authToken ? 'include' : 'omit'
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please login again.');
        } else if (response.status === 403) {
          throw new Error('You do not have permission to view orders.');
        } else if (response.status === 404) {
          throw new Error('Orders endpoint not found.');
        } else {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status}: Failed to fetch orders`);
        }
      }

      const data = await response.json();

      if (data.success) {
        const ordersData = Array.isArray(data.data) ? data.data : [];
        setOrders(ordersData);
        setTotalPages(data.pagination?.totalPages || Math.ceil(ordersData.length / LIMIT) || 1);
        setError(null);
      } else {
        throw new Error(data.message || 'Failed to load orders');
      }

    } catch (error) {
      console.error('Error fetching orders:', error);
      
      let errorMessage = error.message;
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        errorMessage = 'Network error. Please check your connection and try again.';
      }
      
      setOrders([]);
      setTotalPages(1);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Update current user when context changes
  useEffect(() => {
    if (contextUser) {
      setCurrentUser({
        name: contextUser.fullName || contextUser.name || 'Admin User',
        email: contextUser.email || 'admin@telnet.co.id',
        role: contextUser.role || 'Super Admin'
      });
    }
  }, [contextUser]);

  useEffect(() => {
    fetchOrders();
  }, [page, statusFilter, authToken]);

  // Close sidebar when clicking outside on mobile
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Handle search submit
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    fetchOrders();
  };

  // Update order status
  const handleUpdateStatus = async () => {
    if (!selectedOrder || !newStatus) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${selectedOrder.orderNumber}/status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: authToken ? 'include' : 'omit',
        body: JSON.stringify({
          status: newStatus,
          note: statusNote,
          updatedBy: currentUser.name
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to update status`);
      }

      const data = await response.json();
      
      if (data.success) {
        alert('Status berhasil diupdate!');
        setShowStatusModal(false);
        setSelectedOrder(null);
        setNewStatus('');
        setStatusNote('');
        fetchOrders();
      } else {
        throw new Error(data.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Error updating status: ' + error.message);
    }
  };

  // Update order schedule
  const handleUpdateSchedule = async () => {
    if (!selectedOrder) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${selectedOrder.orderNumber}/schedule`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: authToken ? 'include' : 'omit',
        body: JSON.stringify(scheduleData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to update schedule`);
      }

      const data = await response.json();
      
      if (data.success) {
        alert('Jadwal berhasil diupdate!');
        setShowScheduleModal(false);
        setSelectedOrder(null);
        setScheduleData({
          surveyDate: '',
          installationDate: '',
          activationDate: ''
        });
        fetchOrders();
      } else {
        throw new Error(data.message || 'Failed to update schedule');
      }
    } catch (error) {
      console.error('Error updating schedule:', error);
      alert('Error updating schedule: ' + error.message);
    }
  };

  // Cancel order
  const handleCancelOrder = async (order) => {
    if (!window.confirm('Yakin ingin membatalkan order ini?')) {
      return;
    }

    const reason = prompt('Alasan pembatalan:');
    if (!reason) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/orders/${order.orderNumber}/cancel`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: authToken ? 'include' : 'omit',
        body: JSON.stringify({
          reason: reason,
          cancelledBy: currentUser.name
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: Failed to cancel order`);
      }

      const data = await response.json();
      
      if (data.success) {
        alert('Order berhasil dibatalkan!');
        fetchOrders();
      } else {
        throw new Error(data.message || 'Failed to cancel order');
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
      alert('Error cancelling order: ' + error.message);
    }
  };

  // Open modals
  const openStatusModal = (order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setShowStatusModal(true);
  };

  const openScheduleModal = (order) => {
    setSelectedOrder(order);
    setScheduleData({
      surveyDate: order.schedule?.surveyDate ? new Date(order.schedule.surveyDate).toISOString().split('T')[0] : '',
      installationDate: order.schedule?.installationDate ? new Date(order.schedule.installationDate).toISOString().split('T')[0] : '',
      activationDate: order.schedule?.activationDate ? new Date(order.schedule.activationDate).toISOString().split('T')[0] : ''
    });
    setShowScheduleModal(true);
  };

  const openDetailModal = (order) => {
    setSelectedOrder(order);
    setShowDetailModal(true);
  };

  // Pagination controls
  const goToPage = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  // Retry function for failed requests
  const handleRetry = () => {
    fetchOrders();
  };

  // Mobile Header Component with green theme
  const MobileHeader = () => (
    <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Manage Orders</h1>
            <p className="text-xs text-gray-600">{currentUser.role}</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {error && (
            <button
              onClick={handleRetry}
              className="bg-green-600 text-white px-3 py-1 rounded-md hover:bg-green-700 transition text-xs"
            >
              Retry
            </button>
          )}
          <div className="h-8 w-8 bg-green-600 rounded-full flex items-center justify-center">
            <span className="text-white text-sm font-medium">
              {currentUser.name.charAt(0).toUpperCase()}
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  // Sidebar Overlay for Mobile
  const SidebarOverlay = () => (
    <>
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </>
  );

  // Stats Cards Component
  const StatsCards = () => {
    const stats = {
      total: orders.length,
      pending: orders.filter(order => order.status === 'pending').length,
      active: orders.filter(order => order.status === 'active').length,
      revenue: orders.reduce((sum, order) => sum + (order.pricing?.totalAmount || 0), 0)
    };

    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-6">
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Orders</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <Package className="text-blue-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-2xl font-bold text-gray-900">{stats.pending}</p>
            </div>
            <Clock className="text-yellow-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active</p>
              <p className="text-2xl font-bold text-gray-900">{stats.active}</p>
            </div>
            <CheckCircle className="text-green-500" size={32} />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrencyShort(stats.revenue)}</p>
              <button 
                onClick={fetchOrders}
                disabled={isLoading}
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center mt-1"
              >
                <RefreshCw size={12} className={`mr-1 ${isLoading ? 'animate-spin' : ''}`} />
                {isLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
            <DollarSign className="text-purple-500" size={32} />
          </div>
        </div>
      </div>
    );
  };

  // Mobile Order Card Component with green theme
  const MobileOrderCard = ({ order }) => (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-4 mb-4 hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1 min-w-0">
          <button
            onClick={() => openDetailModal(order)}
            className="text-sm font-medium text-green-600 hover:text-green-800 truncate block text-left"
          >
            {order.orderNumber}
          </button>
          <p className="text-sm text-gray-600 truncate">{order.customer?.fullName || 'N/A'}</p>
          <p className="text-xs text-gray-500 truncate">{order.customer?.phone || 'N/A'}</p>
        </div>
        <div className="flex items-center space-x-1 ml-2">
          {getStatusIcon(order.status)}
          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(order.status)}`}>
            {order.status?.replace(/_/g, ' ') || 'Unknown'}
          </span>
        </div>
      </div>
      
      {/* Address section for mobile */}
      <div className="mb-3 p-2 bg-gray-50 rounded">
        <div className="flex items-start">
          <MapPin size={14} className="text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
          <p className="text-xs text-gray-600 leading-relaxed">
            {formatAddressShort(order.address)}
          </p>
        </div>
      </div>
      
      <div className="flex justify-between items-center mb-3">
        <div className="flex-1 min-w-0">
          <p className="text-sm text-gray-900 truncate">{order.packageSnapshot?.name || order.package?.name || 'N/A'}</p>
          <p className="text-xs text-gray-500">{formatDate(order.createdAt)}</p>
        </div>
        <span className="text-sm font-medium text-gray-900 ml-2">
          {formatCurrencyShort(order.pricing?.totalAmount || order.packageSnapshot?.price?.amount || 0)}
        </span>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => openStatusModal(order)}
          className="flex-1 min-w-0 text-green-600 hover:text-green-900 text-xs bg-green-100 px-3 py-2 rounded-md hover:bg-green-200 transition flex items-center justify-center space-x-1"
        >
          <Edit size={12} />
          <span>Status</span>
        </button>
        <button
          onClick={() => openScheduleModal(order)}
          className="flex-1 min-w-0 text-blue-600 hover:text-blue-900 text-xs bg-blue-100 px-3 py-2 rounded-md hover:bg-blue-200 transition flex items-center justify-center space-x-1"
        >
          <Calendar size={12} />
          <span>Schedule</span>
        </button>
        {!['cancelled', 'active', 'installed'].includes(order.status) && (
          <button
            onClick={() => handleCancelOrder(order)}
            className="flex-1 min-w-0 text-red-600 hover:text-red-900 text-xs bg-red-100 px-3 py-2 rounded-md hover:bg-red-200 transition flex items-center justify-center space-x-1"
          >
            <XCircle size={12} />
            <span>Cancel</span>
          </button>
        )}
      </div>
    </div>
  );

  // Desktop Table Component with green theme
  const DesktopTable = () => (
    <div className="bg-white rounded-lg shadow overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order ID</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Package</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Date</th>
            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="bg-white divide-y divide-gray-200">
          {orders.map((order) => (
            <tr key={order._id} className="hover:bg-gray-50 transition-colors">
              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600 cursor-pointer hover:text-green-800"
                  onClick={() => openDetailModal(order)}>
                {order.orderNumber}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div className="flex items-center space-x-2">
                  <User size={16} className="text-gray-400" />
                  <div>
                    <div className="font-medium">{order.customer?.fullName || 'N/A'}</div>
                    <div className="text-gray-500 text-xs">{order.customer?.phone || 'N/A'}</div>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                <div className="flex items-start">
                  <MapPin size={16} className="text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm leading-5 line-clamp-2 break-words">
                      {formatAddress(order.address)}
                    </p>
                  </div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                <div className="flex items-center space-x-2">
                  <Package size={16} className="text-gray-400" />
                  <span>{order.packageSnapshot?.name || order.package?.name || 'N/A'}</span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center space-x-2">
                  {getStatusIcon(order.status)}
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(order.status)}`}>
                    {order.status?.replace(/_/g, ' ') || 'Unknown'}
                  </span>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right font-medium">
                {formatCurrency(order.pricing?.totalAmount || order.packageSnapshot?.price?.amount || 0)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {formatDate(order.createdAt)}
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                <div className="flex space-x-1 justify-center">
                  <button
                    onClick={() => openStatusModal(order)}
                    className="text-green-600 hover:text-green-900 text-xs bg-green-100 px-3 py-2 rounded-md hover:bg-green-200 transition flex items-center space-x-1"
                    title="Update Status"
                  >
                    <Edit size={12} />
                    <span>Status</span>
                  </button>
                  <button
                    onClick={() => openScheduleModal(order)}
                    className="text-blue-600 hover:text-blue-900 text-xs bg-blue-100 px-3 py-2 rounded-md hover:bg-blue-200 transition flex items-center space-x-1"
                    title="Update Schedule"
                  >
                    <Calendar size={12} />
                    <span>Schedule</span>
                  </button>
                  {!['cancelled', 'active', 'installed'].includes(order.status) && (
                    <button
                      onClick={() => handleCancelOrder(order)}
                      className="text-red-600 hover:text-red-900 text-xs bg-red-100 px-3 py-2 rounded-md hover:bg-red-200 transition flex items-center space-x-1"
                      title="Cancel Order"
                    >
                      <XCircle size={12} />
                      <span>Cancel</span>
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <RequireAdminRole user={user} loading={loading}>
      <div className="flex h-screen bg-gray-50 overflow-hidden">
        <SidebarOverlay />
        
        {/* Sidebar - Mobile responsive */}
        <div className={`
          fixed inset-y-0 left-0 z-50 w-64 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}>
          <SidebarHeader 
            activeTab={activeTab} 
            setActiveTab={setActiveTab} 
            currentUser={currentUser}
            onClose={() => setSidebarOpen(false)}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <MobileHeader />

          <main className="flex-1 overflow-auto p-3 sm:p-6">
            {/* Main Panel dengan green theme */}
            <div className="bg-white rounded-lg shadow-xl">
              {/* Header dengan gradient hijau */}
              <div className="bg-gradient-to-r from-green-600 to-green-700 text-white p-4 rounded-t-lg flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center">
                    <Package className="text-green-600" size={24} />
                  </div>
                  <div>
                    <h1 className="font-bold text-xl">Order Management</h1>
                    <div className="flex items-center space-x-2 text-sm">
                      <span className="text-green-100">
                        {orders.length} orders ditemukan • Page {page} dari {totalPages}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={fetchOrders}
                    disabled={isLoading}
                    className="hover:bg-green-700 p-2 rounded transition-colors"
                    title="Refresh"
                  >
                    <RefreshCw size={20} className={isLoading ? 'animate-spin' : ''} />
                  </button>
                </div>
              </div>

              {/* Stats Cards */}
              <div className="p-4 border-b border-gray-200">
                <StatsCards />
              </div>

              {/* Filters and Search */}
              <div className="p-4 border-b border-gray-200">
                <div className="flex flex-col lg:flex-row gap-4">
                  {/* Search */}
                  <form onSubmit={handleSearchSubmit} className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                      <input
                        type="text"
                        placeholder="Cari berdasarkan Order ID, Nama, Telepon, Email..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                      />
                    </div>
                  </form>

                  {/* Filter Toggle untuk Mobile */}
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="lg:hidden flex items-center justify-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <Filter size={20} />
                    <span>Filter</span>
                    {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                  </button>

                  {/* Filters */}
                  <div className={`flex flex-col lg:flex-row gap-2 ${showFilters ? 'block' : 'hidden lg:flex'}`}>
                    <select
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setPage(1);
                      }}
                      className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Error Display */}
              {error && (
                <div className="mx-4 mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <span>⚠️</span>
                      <span>{error}</span>
                    </div>
                    <button
                      onClick={handleRetry}
                      className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700"
                    >
                      Coba Lagi
                    </button>
                  </div>
                </div>
              )}

              {/* Orders Content */}
              <div className="p-4">
                {isLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center">
                      <RefreshCw size={32} className="animate-spin text-green-600 mb-2" />
                      <span className="text-gray-500">Loading orders...</span>
                    </div>
                  </div>
                ) : error ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="text-center max-w-md mx-auto">
                      <div className="text-red-600 mb-4">
                        <span className="text-4xl">⚠️</span>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to Load Orders</h3>
                      <p className="text-gray-600 mb-4">{error}</p>
                      <button
                        onClick={handleRetry}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 transition"
                      >
                        Try Again
                      </button>
                    </div>
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="text-gray-400 mb-4">
                      <Package size={64} className="mx-auto opacity-30" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">No Orders Found</h3>
                    <p className="text-gray-600">
                      {search || statusFilter ? 'Try adjusting your search filters' : 'No orders have been placed yet'}
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Mobile Cards */}
                    <div className="sm:hidden">
                      {orders.map((order) => (
                        <MobileOrderCard key={order._id} order={order} />
                      ))}
                    </div>

                    {/* Desktop Table */}
                    <div className="hidden sm:block">
                      <DesktopTable />
                    </div>
                  </>
                )}

                {/* Pagination */}
                {!error && !isLoading && orders.length > 0 && (
                  <div className="flex flex-col sm:flex-row justify-between items-center mt-6 space-y-3 sm:space-y-0">
                    <button
                      onClick={() => goToPage(page - 1)}
                      disabled={page === 1}
                      className="w-full sm:w-auto px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Sebelumnya
                    </button>
                    <span className="text-sm text-gray-700">
                      Halaman {page} dari {totalPages} ({orders.length} orders)
                    </span>
                    <button
                      onClick={() => goToPage(page + 1)}
                      disabled={page === totalPages}
                      className="w-full sm:w-auto px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Berikutnya
                    </button>
                  </div>
                )}
              </div>
            </div>
          </main>
        </div>

        {/* Status Update Modal */}
        {showStatusModal && selectedOrder && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-auto">
              <div className="p-4 sm:p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Update Status</h3>
                  <button
                    onClick={() => setShowStatusModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ×
                  </button>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Order: <span className="font-semibold">{selectedOrder.orderNumber}</span>
                  </label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm sm:text-base"
                  >
                    {STATUS_OPTIONS.slice(1).map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Note (opsional)
                  </label>
                  <textarea
                    value={statusNote}
                    onChange={(e) => setStatusNote(e.target.value)}
                    rows="3"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm sm:text-base"
                    placeholder="Tambahkan catatan..."
                  />
                </div>
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                  <button
                    onClick={() => setShowStatusModal(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateStatus}
                    className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm sm:text-base"
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Schedule Update Modal */}
        {showScheduleModal && selectedOrder && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md mx-auto">
              <div className="p-4 sm:p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Update Schedule</h3>
                  <button
                    onClick={() => setShowScheduleModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ×
                  </button>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Order: <span className="font-semibold">{selectedOrder.orderNumber}</span>
                  </label>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Survey Date
                  </label>
                  <input
                    type="date"
                    value={scheduleData.surveyDate}
                    onChange={(e) => setScheduleData({...scheduleData, surveyDate: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm sm:text-base"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Installation Date
                  </label>
                  <input
                    type="date"
                    value={scheduleData.installationDate}
                    onChange={(e) => setScheduleData({...scheduleData, installationDate: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm sm:text-base"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Activation Date
                  </label>
                  <input
                    type="date"
                    value={scheduleData.activationDate}
                    onChange={(e) => setScheduleData({...scheduleData, activationDate: e.target.value})}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 text-sm sm:text-base"
                  />
                </div>
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                  <button
                    onClick={() => setShowScheduleModal(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateSchedule}
                    className="w-full sm:w-auto px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 text-sm sm:text-base"
                  >
                    Update
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Order Detail Modal */}
        {showDetailModal && selectedOrder && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-auto max-h-[90vh] overflow-y-auto">
              <div className="p-4 sm:p-5">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Order Details</h3>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-600 text-xl"
                  >
                    ×
                  </button>
                </div>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                  {/* Order Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <Package size={18} />
                      <span>Order Information</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Order ID:</span> {selectedOrder.orderNumber}</div>
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">Status:</span> 
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getStatusColor(selectedOrder.status)}`}>
                          {selectedOrder.status?.replace(/_/g, ' ') || 'Unknown'}
                        </span>
                      </div>
                      <div><span className="font-medium">Created:</span> {formatDateTime(selectedOrder.createdAt)}</div>
                      <div><span className="font-medium">Updated:</span> {formatDateTime(selectedOrder.updatedAt)}</div>
                    </div>
                  </div>

                  {/* Customer Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <User size={18} />
                      <span>Customer Information</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Name:</span> {selectedOrder.customer?.fullName || 'N/A'}</div>
                      <div><span className="font-medium">Phone:</span> {selectedOrder.customer?.phone || 'N/A'}</div>
                      <div><span className="font-medium">Email:</span> {selectedOrder.customer?.email || 'N/A'}</div>
                    </div>
                  </div>

                  {/* Package Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <Package size={18} />
                      <span>Package Information</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Package:</span> {selectedOrder.packageSnapshot?.name || 'N/A'}</div>
                      <div><span className="font-medium">Speed:</span> {selectedOrder.packageSnapshot?.speed || 'N/A'}</div>
                      <div><span className="font-medium">Price:</span> {formatCurrency(selectedOrder.packageSnapshot?.price?.amount || 0)}</div>
                      {selectedOrder.packageSnapshot?.features && (
                        <div>
                          <span className="font-medium">Features:</span>
                          <ul className="list-disc list-inside ml-4 mt-1">
                            {selectedOrder.packageSnapshot.features.map((feature, idx) => (
                              <li key={idx} className="text-xs">{feature}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Address Info */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <MapPin size={18} />
                      <span>Address Information</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      {selectedOrder.address ? (
                        <>
                          <div>
                            <span className="font-medium">Full Address:</span>
                            <p className="mt-1 text-gray-700 leading-relaxed break-words">
                              {selectedOrder.address.fullAddress || 'N/A'}
                            </p>
                          </div>
                          <div className="grid grid-cols-2 gap-4 pt-2">
                            <div><span className="font-medium">City:</span> <br />{selectedOrder.address.city || 'N/A'}</div>
                            <div><span className="font-medium">Province:</span> <br />{selectedOrder.address.province || 'N/A'}</div>
                          </div>
                          <div className="pt-1">
                            <span className="font-medium">Postal Code:</span> {selectedOrder.address.postalCode || 'N/A'}
                          </div>
                        </>
                      ) : (
                        <div className="text-gray-500 italic">Address information not available</div>
                      )}
                    </div>
                  </div>

                  {/* Schedule Info */}
                  {selectedOrder.schedule && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                        <Calendar size={18} />
                        <span>Schedule Information</span>
                      </h4>
                      <div className="space-y-2 text-sm">
                        <div><span className="font-medium">Survey Date:</span> {formatDateTime(selectedOrder.schedule.surveyDate)}</div>
                        <div><span className="font-medium">Installation Date:</span> {formatDateTime(selectedOrder.schedule.installationDate)}</div>
                        <div><span className="font-medium">Activation Date:</span> {formatDateTime(selectedOrder.schedule.activationDate)}</div>
                      </div>
                    </div>
                  )}

                  {/* Pricing Information */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center space-x-2">
                      <DollarSign size={18} />
                      <span>Pricing Information</span>
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div><span className="font-medium">Package Price:</span> {formatCurrency(selectedOrder.pricing?.packagePrice || selectedOrder.packageSnapshot?.price?.amount || 0)}</div>
                      {selectedOrder.pricing?.installationFee > 0 && (
                        <div><span className="font-medium">Installation Fee:</span> {formatCurrency(selectedOrder.pricing.installationFee)}</div>
                      )}
                      {selectedOrder.pricing?.equipmentFee > 0 && (
                        <div><span className="font-medium">Equipment Fee:</span> {formatCurrency(selectedOrder.pricing.equipmentFee)}</div>
                      )}
                      {selectedOrder.pricing?.discount > 0 && (
                        <div><span className="font-medium">Discount:</span> -{formatCurrency(selectedOrder.pricing.discount)}</div>
                      )}
                      <div className="pt-2 border-t border-gray-200">
                        <span className="font-medium text-base">Total Amount:</span> 
                        <span className="text-base font-bold text-gray-900 ml-2">
                          {formatCurrency(selectedOrder.pricing?.totalAmount || selectedOrder.packageSnapshot?.price?.amount || 0)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="w-full sm:w-auto px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 text-sm sm:text-base"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </RequireAdminRole>
  );
};

export default AdminOrders;